import React,{useState,useEffect,useRef} from 'react';
import Icon from '../Text/Icon';
import classNames from 'classnames';

const SLASH_COMMANDS={
    complete: {
        name: '/complete',
        description: 'Add new nodes to extend your flowchart',
        icon: 'plus-circle',
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        shortcut: '⌘⇧1'
    },
    optimize: {
        name: '/optimize',
        description: 'Get suggestions to improve your flowchart',
        icon: 'lightning',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
        shortcut: '⌘⇧2'
    },
    rearrange: {
        name: '/rearrange',
        description: 'Reorganize nodes with a better layout',
        icon: 'arrow-repeat',
        color: 'text-purple-600',
        bgColor: 'bg-purple-100',
        shortcut: '⌘⇧3'
    },
    generate: {
        name: '/generate',
        description: 'Create a new flowchart from description',
        icon: 'stars',
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        shortcut: '⌘⇧4'
    },
    mermaid: {
        name: '/mermaid',
        description: 'Import Mermaid diagram syntax directly',
        icon: 'diagram-3',
        color: 'text-teal-600',
        bgColor: 'bg-teal-100',
        shortcut: '⌘⇧5'
    },
    modify: {
        name: '/modify',
        description: 'Edit existing node content',
        icon: 'pencil',
        color: 'text-orange-600',
        bgColor: 'bg-orange-100',
        shortcut: '⌘⇧6'
    },
    delete: {
        name: '/delete',
        description: 'Remove selected nodes',
        icon: 'trash',
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        shortcut: null
    }
};

function CommandSuggestions({query,onSelect}) {
    const isMac=navigator.userAgent.toUpperCase().includes('MAC');
    const commandQuery=query.toLowerCase().slice(1); // Remove '/'
    const matchingCommands=Object.values(SLASH_COMMANDS).filter(cmd =>
        cmd.name.slice(1).startsWith(commandQuery)
    );

    if(matchingCommands.length===0) return null;

    return (
        <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border rounded-lg shadow-lg overflow-hidden z-50 animate-slide-up" role="listbox" aria-label="Slash commands">
            {matchingCommands.map(cmd => {
                const displayShortcut=cmd.shortcut? (isMac? cmd.shortcut:cmd.shortcut.replace('⌘','Ctrl+').replace('⇧','Shift+')):null;
                return (
                    <div
                        key={cmd.name}
                        onClick={() => onSelect(cmd.name+' ')}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-100 cursor-pointer transition-colors"
                    >
                        <div className={classNames('flex items-center justify-center w-8 h-8 rounded-md',cmd.bgColor)}>
                            <Icon icon={cmd.icon} className={classNames('text-sm',cmd.color)} />
                        </div>
                        <div className="flex-1">
                            <div className="font-semibold text-sm">{cmd.name}</div>
                            <div className="text-xs text-gray-500">{cmd.description}</div>
                        </div>
                        {cmd.shortcut&&<kbd className="px-2 py-1 text-xs font-semibold bg-gray-100 rounded border border-gray-300">{displayShortcut}</kbd>}
                    </div>
                );
            })}
        </div>
    );
}

function ActionIndicator({input}) {
    const [detectedCommand,setDetectedCommand]=useState(null);

    useEffect(() => {
        if(input.startsWith('/')) {
            const commandName=input.split(' ')[0];
            const cmd=SLASH_COMMANDS[commandName.slice(1)];
            setDetectedCommand(cmd);
        } else {
            setDetectedCommand(null);
        }
    },[input]);

    if(!detectedCommand) return null;

    return (
        <div className={classNames(
            'flex items-center gap-2 px-3 py-1.5 rounded-t-lg border-b',
            detectedCommand.bgColor
        )}>
            <Icon icon={detectedCommand.icon} className={classNames('text-sm',detectedCommand.color)} />
            <span className={classNames('text-xs font-semibold',detectedCommand.color)}>
                {detectedCommand.description}
            </span>
        </div>
    );
}

export default function CommandPalette({
    value,
    onChange,
    onSubmit,
    onCancel,
    loading=false,
    placeholder='Ask AI or use / for commands...',
    autoFocus=true
}) {
    const [showSuggestions,setShowSuggestions]=useState(false);
    const inputRef=useRef(null);

    useEffect(() => {
        // Show suggestions when typing slash commands
        if(value.startsWith('/')&&value.indexOf(' ')===-1) {
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    },[value]);

    const handleKeyDown=(e) => {
        const isMac=navigator.userAgent.toUpperCase().includes('MAC');
        const isCtrlOrCmd=isMac? e.metaKey:e.ctrlKey;

        // Cmd/Ctrl+Shift+1-6 for quick command insertion (avoids browser tab shortcuts)
        if(isCtrlOrCmd&&e.shiftKey&&['1','2','3','4','5','6'].includes(e.key)) {
            e.preventDefault();
            const commandMap={
                '1': '/complete ',
                '2': '/optimize ',
                '3': '/rearrange ',
                '4': '/generate ',
                '5': '/mermaid ',
                '6': '/modify '
            };
            onChange(commandMap[e.key]);
            setShowSuggestions(false);
            return;
        }

        // Tab to accept first suggestion (only when typing slash commands)
        if(e.key==='Tab'&&showSuggestions&&value.startsWith('/')) {
            e.preventDefault();
            const commandQuery=value.toLowerCase().slice(1);
            const firstMatch=Object.values(SLASH_COMMANDS).find(cmd =>
                cmd.name.slice(1).startsWith(commandQuery)
            );
            if(firstMatch) {
                onChange(firstMatch.name+' ');
                setShowSuggestions(false);
            }
        }
        // Enter to submit
        else if(e.key==='Enter'&&!e.shiftKey) {
            e.preventDefault();
            onSubmit(e);
        }
        // Escape to clear
        else if(e.key==='Escape') {
            onChange('');
            inputRef.current?.blur();
        }
    };

    const handleSelectSuggestion=(commandText) => {
        onChange(commandText);
        setShowSuggestions(false);
        inputRef.current?.focus();
    };

    return (
        <div className="relative w-full">
            <ActionIndicator input={value} />
            <div className="relative">
                {showSuggestions&&(
                    <CommandSuggestions
                        query={value}
                        onSelect={handleSelectSuggestion}
                    />
                )}
                <div className="flex items-center gap-2 bg-white border rounded-lg shadow-sm focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-all">
                    <input
                        ref={inputRef}
                        type="text"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        disabled={loading}
                        autoFocus={autoFocus}
                        className="flex-1 px-4 py-3 bg-transparent outline-none text-sm"
                    />
                    {loading? (
                        <button
                            onClick={onCancel}
                            className="flex items-center justify-center px-6 py-3 rounded-r-lg font-semibold transition-all bg-red-500 text-white hover:bg-red-600 cursor-pointer"
                            title="Stop AI processing"
                        >
                            <Icon icon="stop-circle" />
                        </button>
                    ):(
                        <button
                            onClick={onSubmit}
                            disabled={!value.trim()}
                            className={classNames(
                                'flex items-center justify-center px-6 py-3 rounded-r-lg font-semibold transition-all',
                                !value.trim()
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    :'bg-primary text-white hover:bg-primary-600 cursor-pointer'
                            )}
                        >
                            <Icon icon="send" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export {SLASH_COMMANDS,CommandSuggestions,ActionIndicator};
